import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import LanguageIcon from "@mui/icons-material/Language";
import BusinessIcon from "@mui/icons-material/Business";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";

import MetricCharts from "../metrics/MetricCharts";
import MetricsTable from "../metrics/MetricsTable";
import BrandSpinner from "../../shared/components/BrandSpinner";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const BRAND_RED = "#C62828";
const MAX_W = 1900;
const CARD_BORDER = "#e3e9f2";

const VISIBLE_CARDS = 5;

/* ===============================
   🔥 NEW ARROW STYLING
================================ */
const ARROW_BUTTON_SX = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  backgroundColor: "#ffffff",
  border: `2px solid ${BRAND_BLUE}`,
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  transition: "all 0.25s ease",

  "&:hover": {
    backgroundColor: BRAND_BLUE,
    transform: "scale(1.1)",
    boxShadow: "0 6px 18px rgba(0,0,0,0.25)",
  },

  "& .MuiSvgIcon-root": {
    color: BRAND_BLUE,
    fontSize: 26,
  },

  "&:hover .MuiSvgIcon-root": {
    color: "#ffffff",
  },

  "&.Mui-disabled": {
    opacity: 0.3,
    border: "1px solid rgba(0,0,0,0.1)",
    boxShadow: "none",
  },
};

const ALL_METRICS = [
  "active_members",
  "new_members",
  "member_revenue",
  "inquiries",
  "leads",
  "qualified_opportunities",
  "rfis_responded_to",
  "prospect_closed_won",
  "prospect_closed_lost",
  "prospects_added",
  "website_visitors",
  "bre_companies",
  "jobs",
  "investment",
  "projects_won",
];

const DASHBOARD_METRICS = [
  {
    key: "active_members",
    label: "Active Members",
    color: BRAND_GREEN,
    iconBg: "#edf6df",
    icon: GroupsIcon,
    description: "Current active member records in the membership module.",
  },
  {
    key: "new_members",
    label: "New Members",
    color: "#1B7F79",
    iconBg: "#def7f4",
    icon: GroupsIcon,
    description: "Membership member records joined or created during the selected date range.",
  },
  {
    key: "member_revenue",
    label: "Total Revenue from Members",
    color: "#2E7D32",
    iconBg: "#e7f6e8",
    icon: AttachMoneyIcon,
    format: "currency",
    description: "Membership dues revenue recorded as paid during the selected date range.",
  },
  {
    key: "inquiries",
    label: "Inquiries",
    color: "#E65100",
    iconBg: "#fff0df",
    icon: TrackChangesIcon,
    description: "Inbound inquiries created during the selected date range.",
  },
  {
    key: "leads",
    label: "Leads - In Conversation With",
    color: "#6A5ACD",
    iconBg: "#f0ebff",
    icon: GroupsIcon,
    description: "Prospects that moved into the In Conversation pipeline stage during the selected date range.",
  },
  {
    key: "qualified_opportunities",
    label: "Qualified Opportunities",
    color: "#6D28D9",
    iconBg: "#ede9fe",
    icon: TrackChangesIcon,
    description: "Prospects marked as qualified opportunities during the selected date range.",
  },
  {
    key: "rfis_responded_to",
    label: "RFIs Responded To",
    color: "#0F766E",
    iconBg: "#ccfbf1",
    icon: FactCheckIcon,
    description: "Prospects moved to Responded to RFI during the selected date range.",
  },
  {
    key: "prospect_closed_won",
    label: "Prospects (Closed Won)",
    color: "#047857",
    iconBg: "#d1fae5",
    icon: CheckCircleIcon,
    description: "Prospects moved to Closed Won during the selected date range.",
  },
  {
    key: "prospect_closed_lost",
    label: "Prospects (Closed Lost)",
    color: "#B91C1C",
    iconBg: "#fee2e2",
    icon: CancelIcon,
    description: "Prospects moved to Closed Lost during the selected date range.",
  },
  {
    key: "prospects_added",
    label: "Prospects Added",
    color: BRAND_GREEN,
    iconBg: "#edf6df",
    icon: ArrowForwardIcon,
    description: "New prospect company records created during the selected date range.",
  },
  {
    key: "website_visitors",
    label: "Website Visitors",
    color: "#17A2B8",
    iconBg: "#e0f8fb",
    icon: LanguageIcon,
    description: "Unique website visitor accounts captured during the selected date range.",
  },
  {
    key: "bre_companies",
    label: "BR&E Companies Tracked",
    color: BRAND_BLUE,
    iconBg: "#e1f3ff",
    icon: BusinessIcon,
    description: "BR&E company records added to tracking during the selected date range.",
  },
  {
    key: "jobs",
    label: "Jobs Created",
    color: "#C47A2C",
    iconBg: "#fff5da",
    icon: WorkOutlineIcon,
    description: "Total jobs created from projects recorded during the selected date range.",
  },
  {
    key: "investment",
    label: "Investment",
    color: "#2E7D32",
    iconBg: "#e7f6e8",
    icon: AttachMoneyIcon,
    format: "currency",
    description: "Total project investment amount recorded during the selected date range.",
  },
  {
    key: "projects_won",
    label: "New Projects",
    color: "#8B3A62",
    iconBg: "#ffe5ef",
    icon: EmojiEventsIcon,
    description: "Projects started during the selected date range, based on each project's created date.",
  },
];


export default function ReportPreview({
  data,
  loading = false,
  selectedMetrics = [],
  hasBre = true,
  hasProspecting = true,
  hasMembership = false,
  showCarousel = true,
  dashboardLayout = false,
  viewMode = "overview",
  rangeLabel = "",
  onExportCsv,
  pdfMode = false,
}) {
  const [index, setIndex] = useState(0);
  const [kpiFilter, setKpiFilter] = useState("All");

  const PROSPECTING_METRICS = [
    "inquiries",
    "prospects_added",
    "leads",
    "qualified_opportunities",
    "rfis_responded_to",
    "prospect_closed_won",
    "prospect_closed_lost",
    "projects_won",
  ];
  const BRE_METRICS = ["jobs", "investment", "bre_companies", "projects_won"];

  if (loading) {
    return (
      <BrandSpinner compact py={4} />
    );
  }

  if (!data?.current) return null;

  /* ===============================
     ACTIVE METRICS
  =============================== */

  let baseMetrics = selectedMetrics.length > 0 ? selectedMetrics : ALL_METRICS;

  if (!hasMembership) {
    baseMetrics = baseMetrics.filter((m) => !["active_members", "new_members", "member_revenue"].includes(m));
  }

  if (!hasBre) {
    baseMetrics = baseMetrics.filter((m) => m !== "bre_companies");
  }

  if (!hasProspecting) {
    const prospectingMetrics = [
      "leads",
      "qualified_opportunities",
      "rfis_responded_to",
      "prospect_closed_won",
      "prospect_closed_lost",
      "prospects_added",
      "website_visitors",
    ];
    baseMetrics = baseMetrics.filter((m) => !prospectingMetrics.includes(m));
  }

  const activeMetrics = baseMetrics;

  const filteredMetrics =
    kpiFilter === "Prospecting"
      ? activeMetrics.filter((m) => PROSPECTING_METRICS.includes(m))
      : kpiFilter === "BR&E"
        ? activeMetrics.filter((m) => BRE_METRICS.includes(m))
        : activeMetrics;
  /* ===============================
     TOTALS
  =============================== */

  const totals = Object.fromEntries(
    Object.entries(data.current.totals || {}).filter(([key]) =>
      activeMetrics.includes(key),
    ),
  );

  /* ===============================
     TIME SERIES
  =============================== */

  const rows = (() => {
    let breRunningTotal = 0;
    return (data.current.time_series || []).map((row) => {
      if (row.bre_companies != null) breRunningTotal += row.bre_companies;
      return {
        period: row.period,
        ...Object.fromEntries(
          Object.entries(row).filter(
            ([key]) => key === "period" || activeMetrics.includes(key),
          ),
        ),
        ...(activeMetrics.includes("bre_companies") ? { bre_companies: breRunningTotal } : {}),
      };
    });
  })();

  const deltas = data.comparison?.delta || {};

  const formatCompactUSD = (n) =>
    `$${Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(n || 0)}`;

  /* ===============================
     SUMMARY CONFIG
  =============================== */

  const SUMMARY_CONFIG = [
    { key: "active_members", label: "Active Members" },
    { key: "new_members", label: "New Members" },
    {
      key: "member_revenue",
      label: "Total Revenue from Members",
      format: "currency",
    },
    { key: "inquiries", label: "Inquiries" },
    { key: "leads", label: "Leads - In Conversation With" },
    { key: "qualified_opportunities", label: "Qualified Opportunities" },
    { key: "rfis_responded_to", label: "RFIs Responded To" },
    { key: "prospect_closed_won", label: "Prospects (Closed Won)" },
    { key: "prospect_closed_lost", label: "Prospects (Closed Lost)" },
    { key: "prospects_added", label: "Prospects Added" },
    {
      key: "website_visitors",
      label: "Website Visitors",
      helper: "Total visits in period",
    },
    { key: "bre_companies", label: "BR&E Companies Tracked" },
    { key: "jobs", label: "Jobs Created" },
    {
      key: "investment",
      label: "Investment",
      format: "currency",
    },
    { key: "projects_won", label: "New Projects" },
  ];

  const summaryRows = SUMMARY_CONFIG.filter((m) =>
    filteredMetrics.includes(m.key),
  ).map((m) => ({
    label: m.label,
    helper: m.helper,
    value:
      m.format === "currency"
        ? formatCompactUSD(totals[m.key])
        : (totals[m.key] ?? 0),
    delta: deltas?.[`${m.key}_pct`],
  }));

  const formatValue = (key, value) => {
    if (key === "investment" || key === "member_revenue") return formatCompactUSD(value);
    return (value ?? 0).toLocaleString();
  };

  const formatSparkPeriod = (period) => {
    const [year, month] = String(period || "").split("-");
    if (!year || !month) return period || "";

    const date = new Date(Number(year), Number(month) - 1, 1);
    if (Number.isNaN(date.getTime())) return period;

    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  const renderSparkTooltip = (metric) => ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const period = payload[0]?.payload?.period || label;

    return (
      <Box
        sx={{
          px: 1.25,
          py: 0.85,
          borderRadius: 1.5,
          border: `1px solid ${CARD_BORDER}`,
          backgroundColor: "#ffffff",
          boxShadow: "0 6px 16px rgba(15, 23, 42, 0.14)",
        }}
      >
        <Typography sx={{ color: BRAND_BLUE, fontSize: 12, fontWeight: 850 }}>
          {formatSparkPeriod(period)}
        </Typography>
        <Typography sx={{ color: "#3d5578", fontSize: 12, fontWeight: 700, mt: 0.25 }}>
          {formatValue(metric.key, payload[0].value)}
        </Typography>
      </Box>
    );
  };

  const formatDelta = (value) => {
    if (value == null) return "No comparison";
    const sign = value > 0 ? "+" : value < 0 ? "-" : "";
    return `${sign}${Math.abs(value).toFixed(1)}%`;
  };

  const renderDeltaPill = (value, tone = "light") => {
    const isDown = value < 0;
    const bg =
      tone === "dark"
        ? isDown
          ? "rgba(239, 68, 68, 0.36)"
          : "rgba(100, 131, 21, 0.48)"
        : isDown
          ? "rgba(220, 38, 38, 0.08)"
          : "rgba(100, 131, 21, 0.1)";
    const color = tone === "dark" ? "#ffffff" : isDown ? "#dc2626" : "#648315";

    return (
      <Box
        sx={{
          ml: "auto",
          px: 1.1,
          py: 0.45,
          borderRadius: 999,
          backgroundColor: bg,
          color,
          fontSize: 12,
          fontWeight: 800,
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        {tone === "light" && value > 0 ? "↗ " : ""}
        {formatDelta(value)}
      </Box>
    );
  };

  const renderKpiCard = (metric) => {
    const Icon = metric.icon;
    const delta = deltas?.[`${metric.key}_pct`];
    const value = totals[metric.key];
    const sparkRows = rows.map((row) => ({
      period: row.period,
      value: row[metric.key] || 0,
    }));

    return (
      <Card
        key={metric.key}
        sx={{
          borderRadius: 2,
          border: `1px solid ${CARD_BORDER}`,
          borderTop: `4px solid ${metric.color}`,
          backgroundColor: "#ffffff",
          boxShadow: "0 4px 12px rgba(15, 23, 42, 0.08)",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 2.5, pb: 1.6 }}>
          <Tooltip title={metric.description || ""} arrow placement="top">
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", mb: 1.7 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: metric.iconBg,
                    color: metric.color,
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </Box>
                {renderDeltaPill(delta)}
              </Box>
              <Typography
                sx={{
                  color: BRAND_BLUE,
                  fontSize: 26,
                  fontWeight: 850,
                  lineHeight: 1,
                }}
              >
                {formatValue(metric.key, value)}
              </Typography>
              <Typography
                sx={{
                  mt: 0.75,
                  color: BRAND_BLUE,
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                {metric.label}
              </Typography>
            </Box>
          </Tooltip>
          <Box sx={{ height: 58, mt: 1.1, mx: -1.5 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sparkRows} margin={{ top: 8, right: 4, left: 4, bottom: 0 }}>
                <defs>
                  <linearGradient id={`spark-${metric.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={metric.color} stopOpacity={0.18} />
                    <stop offset="100%" stopColor={metric.color} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <RechartsTooltip
                  cursor={false}
                  content={renderSparkTooltip(metric)}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (dashboardLayout) {
    const dashboardMetrics = DASHBOARD_METRICS.filter((m) =>
      activeMetrics.includes(m.key),
    );
    const showOverview = viewMode === "overview";
    const showCharts = viewMode === "overview" || viewMode === "charts";
    const showTable = viewMode === "overview" || viewMode === "charts" || viewMode === "table";

    return (
      <Box sx={{ width: "100%", px: { xs: 2, md: 3 } }}>
        {showOverview && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.8 }}>
              <Typography sx={{ color: BRAND_BLUE, fontSize: 15, fontWeight: 850 }}>
                Key Performance Indicators
              </Typography>
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
                {rangeLabel && (
                  <Typography sx={{ color: "#8a98b3", fontSize: 12, display: { xs: "none", sm: "block" } }}>
                    {rangeLabel} • vs. prev. period
                  </Typography>
                )}
                <Box sx={{ display: "flex", border: `1.5px solid ${BRAND_BLUE}`, borderRadius: 2, overflow: "hidden" }}>
                {["All", "Prospecting", "BR&E"].map((label) => {
                  const active = kpiFilter === label;
                  const available =
                    label === "All" ||
                    (label === "Prospecting" && (!hasProspecting ? false : true) && hasBre !== false) ||
                    (label === "BR&E" && hasBre) ||
                    true;
                  if (!available) return null;
                  return (
                    <Box
                      key={label}
                      onClick={() => setKpiFilter(label)}
                      sx={{
                        px: 1.6,
                        py: 0.55,
                        cursor: "pointer",
                        userSelect: "none",
                        fontSize: 12,
                        fontWeight: 700,
                        color: active ? "#ffffff" : BRAND_BLUE,
                        backgroundColor: active ? BRAND_BLUE : "transparent",
                        "&:hover": { backgroundColor: active ? BRAND_BLUE : "rgba(0,51,108,0.08)" },
                        transition: "background 0.15s ease, color 0.15s ease",
                      }}
                    >
                      {label}
                    </Box>
                  );
                })}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 2,
                mb: 3,
              }}
            >
              {dashboardMetrics
                .filter((m) => filteredMetrics.includes(m.key))
                .map(renderKpiCard)}
            </Box>
          </>
        )}

        {showCharts && rows.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.8 }}>
              <Typography sx={{ color: BRAND_BLUE, fontSize: 15, fontWeight: 850 }}>
                Monthly Trends
              </Typography>
            </Box>
            <MetricCharts rows={rows} selectedMetrics={filteredMetrics} pdfMode={pdfMode} />
          </Box>
        )}

        {showTable && (
          <Box sx={{ mb: 4 }}>
            <MetricsTable
              rows={rows}
              totals={totals}
              selectedMetrics={filteredMetrics}
              dashboardLayout
              onExportCsv={onExportCsv}
            />
          </Box>
        )}
      </Box>
    );
  }

  /* ===============================
     CAROUSEL LOGIC
  =============================== */

  const maxIndex = Math.max(0, summaryRows.length - VISIBLE_CARDS);
  const visibleCards = summaryRows.slice(index, index + VISIBLE_CARDS);

  const next = () => {
    if (index < maxIndex) setIndex(index + 1);
  };

  const prev = () => {
    if (index > 0) setIndex(index - 1);
  };

  /* ===============================
     SUMMARY CARD COMPONENT
  =============================== */

  const renderCard = (r) => {
    const isUp = r.delta > 0;
    const deltaColor =
      r.delta == null ? "#999" : isUp ? BRAND_GREEN : BRAND_RED;

    return (
      <Box
        key={r.label}
        sx={{
          p: 2.5,
          borderRadius: 2,
          background: "#fff",
          borderLeft: `6px solid ${BRAND_GREEN}`,
          boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 700,
            color: BRAND_BLUE,
            opacity: 0.85,
            mb: 0.5,
          }}
        >
          {r.label}
        </Typography>

        <Typography
          sx={{
            fontSize: "1.6rem",
            fontWeight: 800,
            color: BRAND_BLUE,
            lineHeight: 1.15,
          }}
        >
          {r.value?.toLocaleString?.() ?? r.value}
        </Typography>

        <Typography
          sx={{
            mt: 0.6,
            fontSize: "0.85rem",
            fontWeight: 700,
            color: deltaColor,
          }}
        >
          {r.delta != null
            ? `${isUp ? "▲" : "▼"} ${Math.abs(r.delta).toFixed(1)}%`
            : "No comparison"}
        </Typography>

        {r.helper && (
          <Typography
            sx={{
              mt: 0.6,
              fontSize: "0.78rem",
              fontWeight: 600,
              color: "#6b7280",
            }}
          >
            {r.helper}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: MAX_W, mx: "auto" }}>
      {/* PERFORMANCE SUMMARY */}

      <Card
        data-pdf-section="kpi"
        sx={{
          mb: 4,
          border: "1px solid #e8eef5",
          borderRadius: 3,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.04)",
          background:
            "linear-gradient(180deg, rgba(231,240,218,0.3) 0%, #ffffff 60%)",
        }}
      >
        <CardContent sx={{ px: 4, py: 3.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, color: BRAND_BLUE, letterSpacing: 0.6 }}
            >
              Performance KPIs
            </Typography>
            <Box sx={{ ml: "auto", display: "flex", gap: 0.5 }}>
              {["All", "Prospecting", "BR&E"].map((label) => {
                const active = kpiFilter === label;
                return (
                  <Box
                    key={label}
                    onClick={() => setKpiFilter(label)}
                    sx={{
                      px: 1.4,
                      py: 0.5,
                      borderRadius: 1.5,
                      cursor: "pointer",
                      userSelect: "none",
                      fontSize: 12,
                      fontWeight: active ? 800 : 600,
                      color: active ? BRAND_BLUE : "#3d5578",
                      backgroundColor: active ? "#eef2f8" : "transparent",
                      "&:hover": { backgroundColor: active ? "#eef2f8" : "rgba(0,51,108,0.06)" },
                      transition: "background 0.15s ease",
                    }}
                  >
                    {label}
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Divider sx={{ mb: 3, opacity: 0.4 }} />

          {!showCarousel && (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: pdfMode
                  ? "repeat(3, 1fr)"
                  : "repeat(auto-fit, minmax(220px, 1fr))",
                gap: 2.5,
              }}
            >
              {summaryRows.map(renderCard)}
            </Box>
          )}

          {showCarousel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {/* LEFT */}
              <IconButton
                onClick={prev}
                disabled={index === 0}
                sx={ARROW_BUTTON_SX}
              >
                <ChevronLeftIcon />
              </IconButton>

              {/* CARDS */}
              <Box
                sx={{
                  flex: 1,
                  display: "grid",
                  gridTemplateColumns: `repeat(${VISIBLE_CARDS}, 1fr)`,
                  gap: 2.5,
                }}
              >
                {visibleCards.map(renderCard)}
              </Box>

              {/* RIGHT */}
              <IconButton
                onClick={next}
                disabled={index === maxIndex}
                sx={ARROW_BUTTON_SX}
              >
                <ChevronRightIcon />
              </IconButton>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* CHARTS */}
      {rows.length > 0 && (
        <>
          <Box sx={{ display: "flex", alignItems: "center", mb: 1.8 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: BRAND_GREEN, mr: 1.25 }} />
            <Typography sx={{ color: BRAND_BLUE, fontSize: 15, fontWeight: 800 }}>
              Monthly Trends
            </Typography>
          </Box>
          <Box data-pdf-section="charts" sx={{ mb: 4 }}>
            <MetricCharts rows={rows} selectedMetrics={filteredMetrics} pdfMode={pdfMode} />
          </Box>
        </>
      )}

      {/* TABLE */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1.8 }}>
        <Box sx={{ width: 3, height: 18, borderRadius: 1, backgroundColor: BRAND_GREEN, mr: 1.25 }} />
        <Typography sx={{ color: BRAND_BLUE, fontSize: 15, fontWeight: 800 }}>
          Monthly Breakdown
        </Typography>
      </Box>
      <Box data-pdf-section="table" sx={{ mb: 4, maxHeight: 380, overflowY: "auto", borderRadius: 2 }}>
        <MetricsTable
          rows={rows}
          totals={totals}
          selectedMetrics={filteredMetrics}
        />
      </Box>
    </Box>
  );
}
